import {
  BoxConfig,
  LicensePlateNoInput,
} from '@euler/app/components/LicensePlateNoInput';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { SafeHaptics } from '@euler/utils';
import {
  ReactElement,
  ReactNode,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { css, useTheme } from 'styled-components';

export type KeyboardMode = 'province' | 'other';

const ProvinceChars: string[] = [
  '川贵云京沪苏浙粤渝湘',
  '冀鲁鄂闽皖赣黑吉辽晋',
  '甘蒙新藏宁桂青豫津',
  '陕琼港澳警学领',
];

const Chars: string[] = ['1234567890', 'QWERTYUP', 'ASDFGHJKL', 'ZXCVBNM'];

const kHorizontalMargin = 3;
const kVerticalMargin = 5;

export type KeyColors = {
  keyBgColor: string;
  funcKeyBgColor: string;
  keyColor: string;
  disabledKeyColor: string;
  doneKeyBgColor?: string;
};

type FuncType = 'backspace' | 'done';
type KeyType = 'normal' | 'func';

type KeyPressEvent = {
  char: string;
  keyType: KeyType;
  funcType?: FuncType;
};

type KeyProps = {
  char: string;
  width: number;
  height: number;
  colors: KeyColors;
  keyType: KeyType;
  funcType?: FuncType;
  backspaceKeySize?: number;
  disabled?: boolean;
  textStyle?: StyleProp<TextStyle>;
  onPress?: (e: KeyPressEvent) => void;
};

const Key = memo(
  ({
    char,
    width,
    height,
    colors,
    keyType,
    funcType,
    style,
    textStyle,
    backspaceKeySize,
    disabled,
    onPress,
    ...props
  }: KeyProps & Omit<ViewProps, 'onPress'>) => {
    const onInternalPress = useCallback(() => {
      SafeHaptics.impact();
      onPress?.({ char, keyType, funcType });
    }, [char, funcType, keyType, onPress]);
    const {
      keyBgColor,
      funcKeyBgColor,
      keyColor,
      disabledKeyColor,
      doneKeyBgColor,
    } = colors;
    return (
      <TouchableOpacity
        onPress={onInternalPress}
        {...props}
        disabled={disabled}
        css={`
          ${keyType === 'normal'
            ? css`
                width: ${width}px;
              `
            : keyType === 'func' && funcType === 'backspace'
            ? css`
                width: ${height}px;
              `
            : ''};
          height: ${height}px;
          border-radius: 5px;
          background-color: ${keyType === 'normal'
            ? keyBgColor
            : funcType === 'done' && !disabled
            ? doneKeyBgColor ?? '#207FE7'
            : funcKeyBgColor};
          margin-left: ${kHorizontalMargin}px;
          margin-right: ${kHorizontalMargin}px;
          padding-left: ${funcType === 'done' ? '10px' : 0};
          padding-right: ${funcType === 'done' ? '10px' : 0};
          align-items: center;
          justify-content: center;
        `}
        style={style}
      >
        {keyType === 'func' && funcType === 'backspace' ? (
          <Icon
            name="ios-backspace-outline"
            size={backspaceKeySize ?? 24}
            color={keyColor}
          />
        ) : (
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Medium};
              font-size: 14px;
              line-height: 16px;
              text-align: center;
              color: ${disabled
                ? disabledKeyColor
                : funcType === 'done'
                ? '#fff'
                : keyColor};
            `}
            style={textStyle}
          >
            {char}
          </Text>
        )}
      </TouchableOpacity>
    );
  },
);

type KeyInfo = {
  id: string;
  char: string;
  type: KeyType;
  func?: FuncType;
  disabled?: boolean;
};

type RowInfo = {
  keys: KeyInfo[];
};

const KeyRow = memo(
  ({
    keys,
    style,
    colors,
    keyWidth,
    keyHeight,
    keyStyle,
    keyTextStyle,
    backspaceKeySize,
    onKeyPress,
    ...props
  }: {
    keyWidth: number;
    keyHeight: number;
    keys: KeyInfo[];
    colors: KeyColors;
    keyStyle?: StyleProp<ViewStyle>;
    keyTextStyle?: StyleProp<TextStyle>;
    backspaceKeySize?: number;
    onKeyPress?: (e: KeyPressEvent) => void;
  } & ViewProps) => {
    return (
      <View
        css={`
          margin-top: ${kVerticalMargin}px;
          margin-bottom: ${kVerticalMargin}px;
          flex-direction: row;
          align-items: center;
          justify-content: center;
        `}
        style={style}
        {...props}
      >
        {keys.map(key => (
          <Key
            key={key.id}
            char={key.char}
            width={keyWidth}
            height={keyHeight}
            keyType={key.type}
            funcType={key.func}
            disabled={key.disabled}
            colors={colors}
            onPress={onKeyPress}
            style={keyStyle}
            textStyle={keyTextStyle}
            backspaceKeySize={backspaceKeySize}
          />
        ))}
      </View>
    );
  },
);

type Render = (args: {
  renderInput: (props?: { boxConfig?: BoxConfig }) => ReactNode;
  renderKeypad: () => ReactNode;
}) => ReactElement;

type KeyboardProps = {
  value?: string;
  doneButtonText?: string;
  defaultSelectedIndex?: number;
  boxConfig?: BoxConfig;
  keyWidth?: number;
  keyHeight?: number;
  keyColors?: KeyColors;
  keyStyle?: StyleProp<ViewStyle>;
  keyTextStyle?: StyleProp<TextStyle>;
  backspaceKeySize?: number;
  width: number;
  onChange?: (value: string) => void;
  onDone?: (value: string) => void;
  onInputIndexChange?: (index: number) => void;
  children?: Render | undefined;
  allowPartialInput?: boolean;
  alwaysEnableDoneButton?: boolean;
} & Omit<ViewProps, 'children'>;

export const LicensePlateNoKeyboard = memo(
  ({
    style,
    width,
    value,
    doneButtonText,
    children,
    defaultSelectedIndex,
    keyColors,
    keyStyle,
    keyTextStyle,
    backspaceKeySize,
    allowPartialInput,
    alwaysEnableDoneButton,
    onDone,
    onChange,
    onInputIndexChange,
    ...props
  }: KeyboardProps) => {
    const theme = useTheme();
    const [inputIndex, setInputIndex] = useState(defaultSelectedIndex ?? 0);
    const mode = useMemo<KeyboardMode>(
      () => (inputIndex === 0 ? 'province' : 'other'),
      [inputIndex],
    );
    const [plateValue, setPlateValue] = useState(value ?? '');
    const isValid =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      alwaysEnableDoneButton ||
      (allowPartialInput === true && plateValue.length > 0) ||
      plateValue.length >= 7;
    const rows = useMemo<RowInfo[]>(() => {
      if (mode === 'province') {
        const charRows: RowInfo[] = ProvinceChars.map(chars => ({
          keys: chars.split('').map(char => ({
            id: char,
            char,
            type: 'normal',
          })),
        }));
        charRows[charRows.length - 2].keys.push({
          id: 'backspace',
          char: '',
          type: 'func',
          func: 'backspace',
        });
        charRows[charRows.length - 1].keys.push({
          id: 'done',
          char: doneButtonText ?? 'Done',
          type: 'func',
          func: 'done',
          disabled: !isValid,
        });
        return charRows;
      } else {
        const charRows: RowInfo[] = Chars.map(chars => ({
          keys: chars.split('').map(char => ({
            id: char,
            char,
            type: 'normal',
          })),
        }));
        charRows[1].keys.push({
          id: 'backspace',
          char: '',
          type: 'func',
          func: 'backspace',
        });
        charRows[charRows.length - 1].keys.push({
          id: 'done',
          char: doneButtonText ?? 'Done',
          type: 'func',
          func: 'done',
          disabled: !isValid,
        });
        return charRows;
      }
    }, [mode, doneButtonText, isValid]);

    // always use the number of keys of the first row to determine
    // the key button dimension.
    const keyCount = Chars[0].length;
    const keyWidth =
      props.keyWidth ?? (width - keyCount * kHorizontalMargin * 2) / keyCount;
    const keyHeight = props.keyHeight ?? keyWidth / (36 / 42);

    const onKeyPress = useCallback(
      (e: KeyPressEvent) => {
        if (e.keyType === 'func') {
          if (e.funcType === 'backspace') {
            const chars = plateValue.split('');
            chars[inputIndex] = '';
            if (inputIndex - 1 >= 0 && inputIndex >= chars.length - 1) {
              setInputIndex(inputIndex - 1);
            }
            const val = chars.filter(x => x).join('');
            setPlateValue(val);
            onChange?.(val);
          } else if (e.funcType === 'done') {
            onDone?.(plateValue);
          }
        } else {
          if (mode === 'province') {
            const chars = plateValue.split('');
            chars[0] = e.char;
            const val = chars.join('');
            setPlateValue(val);
            onChange?.(val);
            setInputIndex(1);
          } else {
            const chars = plateValue.split('');
            chars[inputIndex] = e.char;
            const val = chars.join('');
            setPlateValue(val);
            onChange?.(val);
            if (inputIndex + 1 < 8) {
              setInputIndex(x => x + 1);
            }
          }
        }
      },
      [inputIndex, mode, onChange, onDone, plateValue],
    );

    const onInternalInputIndexChange = useCallback(
      (index: number) => {
        let targetIndex = 0;
        if (index === 0) {
          targetIndex = 0;
        } else if (index > 0) {
          targetIndex = Math.min(plateValue.length, index);
        }
        setInputIndex(targetIndex);
        onInputIndexChange?.(targetIndex);
      },
      [onInputIndexChange, plateValue.length],
    );

    const renderInput = (inputProps?: { boxConfig?: BoxConfig }) => {
      return (
        <LicensePlateNoInput
          value={plateValue}
          selectedIndex={inputIndex}
          onSelectedIndexChange={onInternalInputIndexChange}
          {...inputProps}
        />
      );
    };

    const renderKeypad = () => (
      <>
        {rows.map((row, index) => (
          <KeyRow
            key={`${mode}-${index}`}
            keys={row.keys}
            keyWidth={keyWidth}
            keyHeight={keyHeight}
            colors={keyColors ?? theme.keyboard}
            keyStyle={keyStyle}
            keyTextStyle={keyTextStyle}
            backspaceKeySize={backspaceKeySize}
            onKeyPress={onKeyPress}
          />
        ))}
      </>
    );

    if (typeof children === 'function') {
      return children({ renderInput, renderKeypad });
    }

    return (
      <View style={style} {...props}>
        <View
          css={`
            padding-top: 20px;
            padding-bottom: 10px;
          `}
        >
          {renderInput({ boxConfig: props.boxConfig })}
        </View>
        {renderKeypad()}
      </View>
    );
  },
);
